/* ============================================================
   Datei:          flip-card-grid-v3.js
   Pfad:           assets/flip-card-grid-v3.js
   Begleitdateien: sections/flip-card-grid-v3.liquid
                   snippets/flip-card-grid-v3-info-line.liquid
                   assets/flip-card-grid-v3.css
   Beschreibung:   Custom Element <flip-card-grid-v3>.
                   - Hover-Modus: kein JS nötig (reines CSS wie im Original)
                   - Klick-Modus: Toggle, Fokusführung, inert, Escape
                   - Videos: Autoplay wie im Original; nur bei
                     data-reduced-motion="fade" + Systemeinstellung
                     pausiert mit Controls
   ============================================================ */

(() => {
  if (customElements.get('flip-card-grid-v3')) return;

  const INTERACTIVE = 'a, button, input, select, textarea, video, [tabindex]';

  class FlipCardGridV3 extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.isClickMode = this.dataset.trigger === 'click';
      this.respectsReducedMotion = this.dataset.reducedMotion === 'fade';
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      if (this.isClickMode) {
        this.cards = Array.from(this.querySelectorAll('[data-flip-card-grid-v3-card]'));
        this.cards.forEach((card) => this.setFlipped(card, false));

        this.addEventListener('click', (event) => this.onClick(event), { signal });
        this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });

        // Theme-Editor: ausgewählte Karte mit Vorderseite zeigen
        this.addEventListener('shopify:block:select', (event) => {
          const card = event.target.querySelector?.('[data-flip-card-grid-v3-card]');
          if (card) this.setFlipped(card, false);
        }, { signal });
      }

      this.applyMotionPreference();
      this.motionQuery.addEventListener('change', () => this.applyMotionPreference(), { signal });
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    onClick(event) {
      const card = event.target.closest('[data-flip-card-grid-v3-card]');
      if (!card || !this.contains(card)) return;

      if (event.target.closest('[data-flip-card-grid-v3-toggle]')) {
        event.preventDefault();
        this.setFlipped(card, !card.classList.contains('is-flipped'), { focus: true });
        return;
      }

      // Klick/Tipp auf freie Fläche der Rückseite dreht zurück
      if (
        card.classList.contains('is-flipped') &&
        event.target.closest('[data-flip-card-grid-v3-back]') &&
        !event.target.closest(INTERACTIVE)
      ) {
        this.setFlipped(card, false, { focus: event.detail === 0 });
      }
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      const card = event.target.closest('[data-flip-card-grid-v3-card]');
      if (!card || !card.classList.contains('is-flipped')) return;
      event.preventDefault();
      this.setFlipped(card, false, { focus: true });
    }

    setFlipped(card, flipped, { focus = false } = {}) {
      const front = card.querySelector('[data-flip-card-grid-v3-front]');
      const back = card.querySelector('[data-flip-card-grid-v3-back]');
      if (!front || !back) return;

      card.classList.toggle('is-flipped', flipped);

      // Verdeckte Seite für Tastatur, Screenreader und Klicks deaktivieren
      front.inert = flipped;
      back.inert = !flipped;

      front
        .querySelectorAll('[data-flip-card-grid-v3-toggle]')
        .forEach((button) => button.setAttribute('aria-expanded', String(flipped)));

      if (focus) {
        const target = (flipped ? back : front).querySelector('[data-flip-card-grid-v3-toggle]');
        target?.focus({ preventScroll: true });
      }
    }

    applyMotionPreference() {
      const reduce = this.respectsReducedMotion && this.motionQuery.matches;

      this.querySelectorAll('video').forEach((video) => {
        video.controls = reduce;
        if (reduce) {
          video.pause();
        } else {
          video.muted = true;
          video.play().catch(() => {});
        }
      });
    }
  }

  customElements.define('flip-card-grid-v3', FlipCardGridV3);
})();